import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { speakWelcome } from "../lib/voice";
import { toast } from "sonner";

const LOGO = "/tresses_logo_full.png";

export default function LoginPage() {
    const { login, formatApiError } = useAuth();
    const navigate = useNavigate();
    const [params] = useSearchParams();
    const nextRaw = params.get("next") || "";
    const next = nextRaw.startsWith("/") && !nextRaw.startsWith("//") ? nextRaw : "";
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [submitting, setSubmitting] = useState(false);

    const onSubmit = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        try {
            const data = await login(email, password);
            speakWelcome(data.user.name);
            toast.success(`Welcome back, ${data.user.name}`);
            navigate(data.user.role === "admin" ? "/admin" : (next || "/dashboard"));
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail) || "Login failed");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="min-h-screen grid lg:grid-cols-2 bg-[#FBF6F0]" data-testid="login-page">
            <div className="hidden lg:block relative grain" style={{
                backgroundImage: "url(https://images.unsplash.com/photo-1633681926022-84c23e8cb2d6?auto=format&fit=crop&w=1200&q=80)",
                backgroundSize: "cover",
                backgroundPosition: "center"
            }}>
                <div className="absolute inset-0 bg-gradient-to-t from-[#FBF6F0] via-[#FBF6F0]/40 to-transparent" />
                <div className="absolute bottom-12 left-12 right-12">
                    <h2 className="font-serif-display text-5xl text-[#3D0C1F] leading-tight">
                        Where every<br /><span className="italic gold-text">strand</span> tells a story.
                    </h2>
                </div>
            </div>
            <div className="flex items-center justify-center px-6 py-12 grain">
                <div className="w-full max-w-md">
                    <Link to="/" className="flex items-center gap-4 mb-12">
                        <img src={LOGO} alt="The Tresses — Unisex Salon" className="h-36 w-auto object-contain" data-testid="auth-logo" />
                    </Link>
                    <div className="text-overline">Sign In</div>
                    <h1 className="font-serif-display text-4xl text-[#3D0C1F] mt-3">Welcome back.</h1>
                    <p className="text-sm text-[#7A5262] mt-2">Continue your journey of elegance.</p>

                    <form onSubmit={onSubmit} className="mt-10 space-y-5" data-testid="login-form">
                        <div>
                            <label className="text-overline">Email</label>
                            <input
                                type="email"
                                required
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                className="w-full bg-[#FDF0EC] border border-[#6B1535]/15 px-4 py-3 mt-2 focus:border-[#C29A5B] outline-none text-[#3D0C1F]"
                                placeholder="you@elegance.com"
                                data-testid="login-email"
                            />
                        </div>
                        <div>
                            <label className="text-overline">Password</label>
                            <input
                                type="password"
                                required
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                className="w-full bg-[#FDF0EC] border border-[#6B1535]/15 px-4 py-3 mt-2 focus:border-[#C29A5B] outline-none text-[#3D0C1F]"
                                placeholder="••••••••"
                                data-testid="login-password"
                            />
                        </div>
                        <button type="submit" disabled={submitting} className="btn-gold w-full justify-center disabled:opacity-50" data-testid="login-submit">
                            {submitting ? "Signing in…" : "Sign In"}
                        </button>
                    </form>
                    <div className="text-center mt-4">
                        <Link to="/forgot-password" className="text-xs text-[#7A5262] hover:text-[#8F622C] tracking-wide" data-testid="forgot-password-link">
                            Forgot your password?
                        </Link>
                    </div>
                    <p className="text-sm text-[#7A5262] mt-8 text-center">
                        New to Tresses?{" "}
                        <Link to={next ? `/register?next=${encodeURIComponent(next)}` : "/register"} className="text-[#8F622C] hover:text-[#3D0C1F]" data-testid="login-to-register">Create an account</Link>
                    </p>
                    <div className="text-center mt-6 pt-6 border-t border-[#6B1535]/10">
                        <Link to="/" className="text-[0.65rem] tracking-[0.22em] uppercase text-[#7A5262] hover:text-[#8F622C]" data-testid="login-back-home">
                            ← Back to home
                        </Link>
                    </div>
                </div>
            </div>
        </div>
    );
}
